import type { Property } from '@/domain/properties';
import type { Unit } from '@/domain/units';
import { getAddressDescription } from '@/utils/addresses';
import { Link } from '@tanstack/react-router';
import { useState, type FC } from 'react';

interface Props {
  property: Property;
  unit: Unit;
}

const PropertyCardThumbnails: FC<Props> = ({ property, unit }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const propertyName = getAddressDescription(property.address);

  if (!unit.images || unit.images.length === 0) {
    return null;
  }
  const images = unit.images;

  return (
    <>
      <Link
        to="/units"
        search={{ unitId: unit.id }}
        className="relative h-64 overflow-hidden block"
      >
        <img
          src={images[currentImageIndex].thumbnailUrl}
          alt={propertyName}
          className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
        />
      </Link>
      {/* Navigation Arrows */}
      {images.length > 1 && (
        <>
          <button
            onClick={() =>
              setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length)
            }
            className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10"
            aria-label="Previous image"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>
          <button
            onClick={() => setCurrentImageIndex((prev) => (prev + 1) % images.length)}
            className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10"
            aria-label="Next image"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
            </svg>
          </button>

          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex space-x-1.5 z-10">
            {images.map((image, idx) => (
              <div
                key={`thumbnail-indicator-${idx}`}
                className={`w-1.5 h-1.5 rounded-full shadow-sm ${
                  idx === currentImageIndex ? 'bg-white' : 'bg-white/50'
                }`}
              />
            ))}
          </div>
        </>
      )}
    </>
  );
};

export default PropertyCardThumbnails;
